// =============================================================================
// FIRST-PARTY LINK ATTRIBUTION — the browser half of link tracking.
//
// Flat next to App.js on purpose. There is no src/utils/, no src/services/ and no
// src/hooks/ in this app; 40 flat components and a handful of flat files is the
// house shape and matching it is worth more than tidiness.
//
// WHAT THIS ANSWERS: "where did this visitor come from, and did they care?"
// A coded link (animates.app/zh8b, ?s=zh8b, api.animates.app/r/zh8b) is recorded
// as a CLICK; the handful of things a visitor does that indicate real intent are
// recorded as ACTIONS. Clicks say who showed up. Actions say who cared.
//
// 🔒 EVERY STORAGE READ AND WRITE IS IN ITS OWN try/catch AND WORKS WHEN IT THROWS.
//    Safari private browsing and "block all cookies" make localStorage throw on
//    ACCESS — they do not return null. Tracking that breaks the page is strictly
//    worse than no tracking, so every public function here is total: it swallows
//    everything, returns a sane value, and never blocks a click.
//
// 🔴 EXACTLY ONE link_clicks ROW PER PAGE LOAD. Three code paths can produce one
//    and they are mutually exclusive by construction — see THE DOUBLE-COUNT GUARD.
//
// Wire contract: a-server/routes/links.js (POST /l/visit, POST /l/action,
// GET /l/resolve/:slug). snake_case both directions, mirroring the column names.
// =============================================================================

const API = process.env.REACT_APP_SERVER_API;

// ── Storage keys ─────────────────────────────────────────────────────────────
// New keys only, all `am_` prefixed. NOT touched by anything here:
// `token`, `user`, `upcoming_event` (the session trio) and `affiliate_ref`
// (four live revenue-share relationships depend on that key and its shape).
const KEY_VISITOR_ID = 'am_visitor_id';   // localStorage  — a random first-party id, durable
const KEY_LAST_SOURCE = 'am_last_source';  // localStorage  — {code, platform, placement, at}
const KEY_CLICK_ID = 'am_click_id';     // sessionStorage — the click this tab belongs to
const KEY_VISIT_SENT = 'am_visit_sent';   // sessionStorage — which code this tab already reported

// 30 days, LAST TOUCH. A new coded click overwrites the stored source; a stored
// source older than this is treated as absent, so an untagged visitor two months
// after an Instagram click counts as organic rather than borrowing old credit.
const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

// -----------------------------------------------------------------------------
// 🔴 THE DOUBLE-COUNT GUARD — the single most important thing in this file.
//
// `animates.app/<slug>` is served by AffiliateLanding, which asks
// GET /l/resolve/:slug and the SERVER writes the click row for it. If initTracking
// also POSTed /l/visit on those URLs, every bio-link click would be counted twice
// and every dashboard number would be wrong by a different factor per platform.
//
// So: when the path is a bare single segment that is not one of our own static
// routes, this module writes NOTHING and hands the page to AffiliateLanding.
// `beaconSkipped` below records that hand-off, and recordUnresolvedSlug() is the
// other half of it — it fires only when the hand-off happened AND the server said
// the slug was neither a code nor an affiliate, which is the one case where the
// resolver deliberately writes no row of its own.
//
// ⚠️ THIS LIST MIRRORS src/App.js AND App.js IS THE SOURCE OF TRUTH. Add a new
//    single-segment route there → add it here in the same edit, or the first visit
//    to that page is silently handed to AffiliateLanding's accounting and never
//    logged. (Multi-segment routes — /e/:slug, /signup/continue, /reset-password/…
//    — need no entry: the shape test below only ever fires on ONE segment.)
//    🔒 And remember the other half of a new single-segment route: it must also go
//       into RESERVED_DISPLAY_NAMES in a-server/utils/displayNameRules.js, or a
//       user can register a display name that shadows it.
const STATIC_SINGLE_SEGMENT_ROUTES = new Set([
    'coming-soon',
    'terms-of-service',
    'privacy',
    'cookies',
    'child-safety',
    'signin',
    'forgot-password',
    'home',
    'change-email',
    'change-password',
    'manage-subscription',
    'affiliate',
    'rules',
    'how-to-friends',
    'how-to-community',
    'how-to-power-level',
    'how-to-messages',
    'how-to-profile',
    'how-to-events',
    'pause-account',
    'delete-account',
    'signup',
    'signup-success',
    'signup-cs',
    'signup-confirmation',
    'signup-confirmation-community',
    'create-event-opportunity',
]);

// The union of the two namespaces that live on a single segment: link codes
// ([a-z0-9._-], 2-24 — a-server/utils/linkCodes.js CODE_PATTERN) and display
// names ([A-Za-z0-9._], 2-30). Deliberately the same shape as the server's own
// SLUG_SHAPE in routes/links.js, because "would /l/resolve look at this?" is
// exactly the question being asked.
//
// ⚠️ THE HYPHEN IS LOAD-BEARING and is a deliberate widening of the shape in the
//    round's brief. Codes are allowed to contain '-' (01-DESIGN.md's own example
//    of a code the owner mints from the dashboard is `ig-launch-ad`), so a
//    hyphen-less test would let /ig-launch-ad both POST a visit here AND be
//    resolved by AffiliateLanding — the exact double count this guard exists to
//    prevent, appearing the first time he mints a readable code.
const SINGLE_SEGMENT_SHAPE = /^[A-Za-z0-9._-]{2,30}$/;

// -----------------------------------------------------------------------------
// 🔴 NEVER PUT A SECRET IN THE ANALYTICS TABLE.
//
// Two routes carry a credential in the URL itself — /reset-password/:id/:token
// and /verify-email/:id/:token — and `link_clicks.landing_path` is an
// append-forever column that a dashboard reads. A password-reset token sitting in
// an analytics row is a real credential leak with a long shelf life, and nothing
// downstream would ever flag it.
//
// The page view is still worth counting, so the path is TRUNCATED to its route
// prefix and the query string is dropped, rather than the visit being discarded.
// ⚠️ Add any future route whose URL contains a token, id or email to this list.
// -----------------------------------------------------------------------------
const SECRET_PATH_PREFIXES = ['/reset-password', '/verify-email'];

// Query keys that must never be stored, whatever page they appear on. `session_id`
// is real and live: Stripe returns the visitor to
// /manage-subscription?checkout=success&session_id=cs_... (ManageSubscription.js).
// The key is KEPT and only its value replaced, so the dashboard can still see that
// the parameter was there.
const SECRET_QUERY_KEYS = ['session_id', 'token', 'access_token', 'id_token', 'code', 'key', 'secret', 'password', 'email'];

function redactPath(pathname) {
    // ⚠️ Lower-cased before matching. React Router v6 matches case-insensitively,
    //    so /Reset-Password/2998/<token> renders the reset page exactly as the
    //    lower-case form does — and a case-sensitive test here would let the token
    //    straight through into the analytics table.
    const path = pathname || '/';
    const lower = path.toLowerCase();
    for (let i = 0; i < SECRET_PATH_PREFIXES.length; i += 1) {
        const prefix = SECRET_PATH_PREFIXES[i];
        if (lower === prefix || lower.indexOf(prefix + '/') === 0) return prefix + '/[redacted]';
    }
    return path;
}

function isSecretPath(pathname) {
    return redactPath(pathname) !== (pathname || '/');
}

/** The query string with any sensitive VALUE replaced. Shape and order preserved. */
function redactQuery(search) {
    try {
        const raw = search || '';
        if (!raw) return null;
        let touched = false;
        const out = raw.replace(/^\?/, '').split('&').map((pair) => {
            const eq = pair.indexOf('=');
            const key = (eq === -1 ? pair : pair.substring(0, eq)).toLowerCase();
            if (SECRET_QUERY_KEYS.indexOf(key) === -1) return pair;
            touched = true;
            return (eq === -1 ? pair : pair.substring(0, eq)) + '=[redacted]';
        }).join('&');
        return touched ? '?' + out : raw;
    } catch (_) {
        return null; // never let a redaction failure send the un-redacted value
    }
}

/**
 * A referrer safe to store. The reset-password and verify-email pages link onward
 * within the site, so `document.referrer` can carry the very token that
 * redactPath() just scrubbed out of the path.
 */
function safeReferrer() {
    try {
        const raw = document.referrer || '';
        if (!raw) return null;
        let path = '';
        try { path = new URL(raw).pathname; } catch (_) { return null; }
        return isSecretPath(path) ? null : raw;
    } catch (_) {
        return null;
    }
}

// link_clicks.entry — validated server-side against the same four strings.
const ENTRY_QUERY = 'query';    // arrived with ?s= or with campaign/ad parameters
const ENTRY_DIRECT = 'direct';   // arrived with nothing on the URL — organic, and a first-class row
const ENTRY_PATH = 'path';     // arrived as animates.app/<slug>

// Campaign and ad-network parameters. utm_* and the click ids are ALSO parsed
// server-side out of the raw query string we send, so this list only has to answer
// "was this visit tagged at all?" — it is not the parser.
// ⚠️ KEEP IN STEP WITH AD_CLICK_PARAMS in a-server/routes/links.js. A paid click
//    whose network is missing here is classified entry='direct' and lands in the
//    organic bucket — the server still parses and stores the id, but the entry
//    kind is decided on this side and is what the dashboard groups by.
const TAGGED_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
    'fbclid', 'gclid', 'ttclid', 'twclid', 'rdt_cid', 'li_fat_id',
    'msclkid', 'epik', 'sccid', 'igshid',
];

// Set when initTracking() handed this page load to AffiliateLanding. Module-level
// rather than storage: it describes THIS page load only, and it must not survive
// a reload the way a storage flag would.
let beaconSkipped = false;

// initTracking is called from index.js, which runs exactly once. This is belt and
// braces against a future second caller, not a real hazard today.
let initialised = false;

// 🔒 IN-MEMORY MIRRORS, and they are not redundancy for its own sake. With storage
//    disabled (Safari private browsing, "block all cookies") every write below is
//    a silent no-op, so a click id read straight back out of sessionStorage would
//    be null and every action on the page would land unattributed. These keep one
//    page load internally consistent even when nothing can be persisted.
// Set when this page load opted out of tracking entirely (the in-app webview
// marker). Everything public in this module then becomes a no-op, so a component
// that fires an action on such a page cannot mint a visitor id behind our back.
let trackingDisabled = false;

let memoryVisitorId = null;
let memoryClickId = null;
let memorySource = null;


// -----------------------------------------------------------------------------
// STORAGE — every accessor total. `window.localStorage` can throw on the property
// access itself, not just on get/set, so the whole expression sits inside the try.
// -----------------------------------------------------------------------------

function readLocal(key) {
    try { return window.localStorage.getItem(key); } catch (_) { return null; /* storage disabled */ }
}

function writeLocal(key, value) {
    try { window.localStorage.setItem(key, value); } catch (_) { /* storage disabled — proceed without persistence */ }
}

function readSession(key) {
    try { return window.sessionStorage.getItem(key); } catch (_) { return null; /* storage disabled */ }
}

function writeSession(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (_) { /* storage disabled — proceed without persistence */ }
}

function clearSession(key) {
    try { window.sessionStorage.removeItem(key); } catch (_) { /* storage disabled */ }
}


// -----------------------------------------------------------------------------
// VISITOR ID — a random first-party string, not a cookie and not a person. It only
// has to be stable enough to group one browser's rows together; the server never
// looks inside it and shape-gates it to [A-Za-z0-9_-]{8,36}, which a v4 UUID meets.
// -----------------------------------------------------------------------------

function randomUuid() {
    // crypto.randomUUID is the right answer where it exists, but CRA 5's
    // browserslist (">0.2%, not dead") still includes browsers that predate it,
    // so it can never be the only path.
    try {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    } catch (_) { /* fall through */ }
    try {
        if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
            const bytes = new Uint8Array(16);
            window.crypto.getRandomValues(bytes);
            bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
            bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
            const hex = [];
            for (let i = 0; i < 16; i += 1) hex.push((bytes[i] + 0x100).toString(16).substring(1));
            return hex.slice(0, 4).join('') + '-' + hex.slice(4, 6).join('') + '-' +
                hex.slice(6, 8).join('') + '-' + hex.slice(8, 10).join('') + '-' + hex.slice(10, 16).join('');
        }
    } catch (_) { /* fall through */ }
    // Last resort. Not cryptographic and does not need to be — a collision costs
    // two visitors sharing a row grouping in a private dashboard.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
    });
}

function ensureVisitorId() {
    // Shape-checked against the server's own VISITOR_ID_SHAPE, not just length: a
    // corrupt or hand-edited value is silently dropped to NULL server-side, which
    // would ungroup every row this browser writes.
    const existing = readLocal(KEY_VISITOR_ID);
    if (existing && /^[A-Za-z0-9_-]{8,36}$/.test(existing)) return existing;
    if (memoryVisitorId) return memoryVisitorId;
    const fresh = randomUuid();
    memoryVisitorId = fresh;
    writeLocal(KEY_VISITOR_ID, fresh);
    // Returned even when the write failed (private mode). The id then lasts one
    // page load, which still groups a visit with the actions taken on it.
    return fresh;
}


// -----------------------------------------------------------------------------
// THE STORED SOURCE — 30 days, last touch.
// -----------------------------------------------------------------------------

function isWithinAttributionWindow(at) {
    return typeof at === 'number' && at > 0 && (Date.now() - at) < ATTRIBUTION_WINDOW_MS;
}

/**
 * The source this visitor is currently attributed to, or null.
 * Null means "organic" and is a real answer, not a failure.
 */
export function getSource() {
    try {
        // memorySource, when set, is this page load's own write and is therefore
        // never staler than storage — which matters when writes are refused
        // (quota, private mode) but reads still return an older value.
        const raw = memorySource ? null : readLocal(KEY_LAST_SOURCE);
        const parsed = memorySource || (raw ? JSON.parse(raw) : null);
        if (!parsed || !parsed.code) return null;
        // ⚠️ THE ONE CONDITION. A visitor who arrives untagged but still carries a
        //    live stored source KEEPS it — that is the whole point of a 30-day
        //    window. Only an EXPIRED source is treated as absent.
        if (!isWithinAttributionWindow(parsed.at)) return null;
        return parsed;
    } catch (_) {
        return null; // malformed or unreadable storage — organic
    }
}

/**
 * Records a new last-touch source. A fresh coded click always overwrites,
 * including with a null platform (the /r/<code> hard redirect knows the code but
 * cannot tell the browser the platform).
 */
export function rememberSource(source) {
    try {
        if (!source || !source.code) return;
        memorySource = {
            code: source.code,
            platform: source.platform || null,
            placement: source.placement || null,
            at: Date.now(),
        };
        writeLocal(KEY_LAST_SOURCE, JSON.stringify(memorySource));
        notifySourceChange();
    } catch (_) { /* storage disabled — attribution degrades to this page load */ }
}

// -----------------------------------------------------------------------------
// 🔑 WHY THERE IS A SUBSCRIPTION HERE, in an app with no state container.
//
//    On the FIRST landing at /?s=zh8b the order is: initTracking fires the beacon,
//    React renders, and the response (which carries the platform and placement)
//    arrives a few hundred milliseconds LATER. AppStoreButtons is on that page.
//    Read once at render time it would build plain, uncampaigned store links on the
//    single most important page view in the whole funnel — the one that just came
//    from the ad — and nothing would ever correct them.
//
//    Twelve lines and one setState is the whole fix. It is not a store, it is not a
//    context, and nothing else in the app needs to know it exists.
// -----------------------------------------------------------------------------

const sourceListeners = [];

/** Subscribe to source changes. Returns an unsubscribe function. */
export function onSourceChange(listener) {
    if (typeof listener !== 'function') return function noop() {};
    sourceListeners.push(listener);
    return function unsubscribe() {
        const index = sourceListeners.indexOf(listener);
        if (index !== -1) sourceListeners.splice(index, 1);
    };
}

function notifySourceChange() {
    for (let i = 0; i < sourceListeners.length; i += 1) {
        try { sourceListeners[i](); } catch (_) { /* a listener must never break the writer */ }
    }
}

/**
 * Marks this browser session as already counted, and remembers which click row it
 * belongs to so downstream actions can be joined to it.
 *
 * `am_visit_sent` holds the CODE that was reported rather than a bare '1': a
 * second, DIFFERENT coded link opened in the same tab session is a real new touch
 * and must be recorded, while a reload of the same one must not.
 */
export function markVisitSent(clickId, code) {
    if (clickId) {
        memoryClickId = Number(clickId) || null;
        writeSession(KEY_CLICK_ID, String(clickId));
    } else {
        // 🔒 NO ID MEANS FORGET THE OLD ONE. A server insert that failed must not
        //    leave the PREVIOUS visit's click id in place: every action on this
        //    page would attach to it, and landing_view would flip `rendered` on a
        //    row from a different visit.
        clearClickId();
    }
    writeSession(KEY_VISIT_SENT, code || '-');
}

/**
 * 🔒 Forgetting the previous landing's click id is not tidiness. A new page load
 *    that is about to write its own click row must not leave the old id in place:
 *    if the new insert fails, every action on this page would attach to a click
 *    from a different visit — and landing_view would flip `rendered` on that older
 *    row, silently corrupting the ghost filter.
 */
function clearClickId() {
    memoryClickId = null;
    clearSession(KEY_CLICK_ID);
}

function currentClickId() {
    const raw = readSession(KEY_CLICK_ID);
    if (raw) {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0) return n;
    }
    return memoryClickId;
}


// -----------------------------------------------------------------------------
// THE WIRE. fetch, not axios, and every call is fire-and-forget.
// -----------------------------------------------------------------------------

/**
 * 🔑 keepalive: true is the reason this is fetch and not axios. A store-button tap
 *    navigates the page away, and an in-flight XHR is CANCELLED on navigation — so
 *    the single most valuable event in the whole system is the one most likely to
 *    be lost. keepalive lets the request outlive the document.
 */
function post(path, payload) {
    try {
        if (!API) return null; // no REACT_APP_SERVER_API baked in — no-op, never a request to "undefined/..."
        if (typeof fetch !== 'function') return null;
        return fetch(API + path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
            credentials: 'omit',
        }).catch(() => null);
    } catch (_) {
        return null;
    }
}

/**
 * A downstream event: store_click_ios · store_click_android · email_capture ·
 * signup_click · landing_view · event_view · signup_completed · outbound.
 * Anything else is rejected server-side with no row written.
 *
 * ⚠️ The wire field is `event_type`, NOT `type` — a-server/routes/links.js reads
 *    body.event_type and 400s on anything it does not recognise.
 *
 * Never throws, never blocks, never returns anything the caller has to handle.
 */
export function recordAction(type, detail) {
    try {
        if (trackingDisabled) return;
        post('/l/action', {
            visitor_id: ensureVisitorId(),
            click_id: currentClickId(),
            event_type: type,
            detail: detail || null,
            path: redactPath((window.location && window.location.pathname) || '/'),
        });
    } catch (_) { /* a tracking call must never throw into the app */ }
}

/**
 * Writes one click row, adopts the id it comes back with, and flags the render.
 * The single place a /l/visit is sent, so the three shapes cannot drift apart.
 *
 * 🔑 `source_known` IS CHECKED. The server records whatever code it was handed —
 *    a typo is data — but it says plainly whether that code matched a live source,
 *    and only a real one is allowed to become this visitor's stored attribution.
 *    Without that check `?s=anything` would set a 30-day source, ride along on the
 *    store links' campaign parameters and land in email_list.source_code.
 */
function sendVisit(code, entry) {
    clearClickId();

    const secret = isSecretPath(window.location.pathname);

    const request = post('/l/visit', {
        code: code || null,
        entry: entry,
        path: redactPath(window.location.pathname),
        query: secret ? null : redactQuery(window.location.search),
        referrer: safeReferrer(),
        visitor_id: ensureVisitorId(),
        tz_offset: new Date().getTimezoneOffset(),
    });

    if (!request) return null;

    return request
        .then((res) => (res && res.ok ? res.json() : null))
        .then((body) => {
            if (!body || body.success !== true) return null;
            markVisitSent(body.click_id, code || '-');
            if (code && body.source_known) {
                rememberSource({ code: code, platform: body.platform, placement: body.placement });
            }
            // 🔑 THE GHOST FILTER (01-DESIGN.md Tier 1). The click row exists the
            //    moment the URL is hit; `rendered` flips only when the bundle
            //    actually ran. The gap between the two IS the bot / preview /
            //    accidental-tap rate, and nothing but the browser can report it.
            recordAction('landing_view');
            return body;
        })
        .catch(() => null);
}

/**
 * The one case the resolver deliberately does not log: AffiliateLanding asked
 * /l/resolve/:slug, the answer was `none`, and the server wrote no row (its own
 * comment says the web beacon covers it). A dead code IS data — it means a link
 * somewhere in the world is wrong — so it is recorded here with the typed slug in
 * code_raw and source_id NULL.
 *
 * 🔒 Gated on `beaconSkipped` so it can only ever fire on a page load where
 *    initTracking wrote nothing. That is what keeps "one row per page load" true.
 *    It goes through sendVisit so the row gets its landing_view like any other —
 *    a dead-link row that could never be marked rendered would show up as 100%
 *    bot traffic on the very report meant to identify a wrong link.
 */
export function recordUnresolvedSlug(slug) {
    try {
        if (!beaconSkipped || !slug) return;
        beaconSkipped = false; // one row per page load, even if this is called twice
        sendVisit(String(slug).toLowerCase(), ENTRY_PATH);
    } catch (_) { /* never throws */ }
}

/** The id this browser is grouped by. Exported so callers never re-read storage. */
export function getVisitorId() {
    try {
        return ensureVisitorId();
    } catch (_) {
        return null;
    }
}


// -----------------------------------------------------------------------------
// INIT
// -----------------------------------------------------------------------------

function readParam(search, name) {
    try {
        const value = new URLSearchParams(search).get(name);
        return value ? value.trim() : null;
    } catch (_) {
        return null;
    }
}

function isTagged(search) {
    try {
        const params = new URLSearchParams(search);
        return TAGGED_PARAMS.some((name) => !!params.get(name));
    } catch (_) {
        return false;
    }
}

/** True when this path belongs to AffiliateLanding rather than to a real route. */
function isResolverPath(pathname) {
    // ⚠️ TRAILING SLASHES ARE NOT COSMETIC HERE. react-router 6.16 compiles
    //    /:displayName to ^/([^/]+)\/*$, so `/zh8b/` reaches AffiliateLanding
    //    exactly as `/zh8b` does — and AffiliateLanding.getSlug() strips the slash
    //    before resolving. If this test disagreed with that one, the page would
    //    post a visit AND be resolved: two click rows and two landing_views for a
    //    single load, which is precisely the double count this guard exists to
    //    stop. Normalise the same way getSlug does.
    const path = (pathname || '/').replace(/\/+$/, '');
    if (!path) return false;                                 // "/" — the landing page
    if (path.indexOf('/', 1) !== -1) return false;           // more than one segment
    const segment = path.replace(/^\//, '');
    if (!SINGLE_SEGMENT_SHAPE.test(segment)) return false;
    // React Router v6 matches case-insensitively, so /Privacy renders Privacy and
    // must not be mistaken for a slug.
    return !STATIC_SINGLE_SEGMENT_ROUTES.has(segment.toLowerCase());
}

/**
 * Called once, from index.js, before render.
 *
 * 🔒 Deliberately NOT a component in the route tree. Routing is the one part of
 *    this app that is expensive to get wrong, this reads window.location and needs
 *    no router at all, and index.js runs exactly once — which is the semantics we
 *    want.
 */
export function initTracking() {
    try {
        if (initialised) return;
        initialised = true;

        const search = (window.location && window.location.search) || '';
        const pathname = (window.location && window.location.pathname) || '/';

        // ⚠️ IN-APP WEBVIEW, NOT A WEB VISIT. ?embed=1 is what the iOS and Android
        //    how-to screens append when they render these pages inside the app
        //    (HowToLayout.js). Counting those would pour the app's own traffic
        //    into the organic bucket at a rate proportional to app usage, and it
        //    would be indistinguishable from a real visitor. Checked BEFORE the
        //    visitor id is minted so a webview leaves no trace at all. Flagged in
        //    the report for veto — it is two lines to remove.
        if (readParam(search, 'embed') === '1') {
            trackingDisabled = true;
            return;
        }

        ensureVisitorId();

        // Lowercased to match the server, which normalises every code before it
        // looks it up. Storing '?s=ZH8B' raw would split the same campaign across
        // two buckets in both store consoles.
        const rawCode = readParam(search, 's');
        const code = rawCode ? rawCode.toLowerCase() : null;

        // Once per browser session, plus any NEW code: a reload must not count
        // twice, but a second, DIFFERENT coded link opened in the same tab is a
        // real new touch and has to be recorded.
        const alreadySent = readSession(KEY_VISIT_SENT);
        const alreadyCounted = !!alreadySent && (!code || alreadySent === code);

        // ── 1. The hard 302 already logged this click ────────────────────────
        // GET /r/<code> writes the row server-side and hands it back as
        // ?s=<code>&c=<click_id>. Adopt that id; posting a visit here would be the
        // same double count in a different disguise. (platform/placement are
        // unknown on this path — the redirect has no way to carry them — so the
        // store links fall back to the code as utm_source.)
        const handedBackClickId = readParam(search, 'c');
        if (handedBackClickId && /^[0-9]{1,15}$/.test(handedBackClickId)) {
            // A RELOAD of the same landing URL is skipped; a genuinely different
            // click id — a second QR scan in the same tab session — is adopted.
            if (alreadyCounted && currentClickId() === Number(handedBackClickId)) return;
            markVisitSent(handedBackClickId, code || '-');
            // ⚠️ THE HONEST RESIDUAL. There is no source_known on this path — the
            //    row was written server-side before the browser existed — so the
            //    code is only SHAPE-checked, matching a-server/utils/linkCodes.js
            //    CODE_PATTERN. A code that came through our own /r/ redirect is
            //    trusted without a second lookup; the worst case is a retired code
            //    carrying a campaign string into the store consoles for 30 days.
            //    The alternative, a second lookup, would write a second click row.
            if (code && /^[a-z0-9][a-z0-9._-]{1,23}$/.test(code)) {
                rememberSource({ code: code, platform: null, placement: null });
            }
            recordAction('landing_view');
            return;
        }

        // ── 2. Hand a bare single segment to AffiliateLanding ────────────────
        if (isResolverPath(pathname)) {
            beaconSkipped = true;
            return;
        }

        // ── 3. The ordinary page load ────────────────────────────────────────
        if (alreadyCounted) return;

        const entry = (code || isTagged(search)) ? ENTRY_QUERY : ENTRY_DIRECT;

        // Written before the request so a re-entrant call cannot double-post, and
        // cleared again on failure so a transient network error does not cost this
        // visitor's attribution for the rest of the session.
        markVisitSent(null, code || '-');

        const request = sendVisit(code, entry);

        if (!request) {
            clearSession(KEY_VISIT_SENT); // nothing was sent — do not pretend otherwise
            return;
        }

        request.then((body) => {
            if (!body) clearSession(KEY_VISIT_SENT);
        });
    } catch (_) { /* tracking must never break the page */ }
}
